import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authorizeClient, client, signalingClient } from '../../networking/client';
import { RootState } from '../store';

export interface ChatIState {
  allConversations: Array<any>;
  filteredAllConversations: Array<any>;
  currentConversation: any;
  candidate_offer: Array<any>;
  conversationParticipants: any;
  chatConversation: any;
  inboxChannel: string;
  chatActiveTab: number;
  conversationStatus: string;
  customers: any;
  iceServerData: any;
  // sdp_offer: any;
  action: {
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: any;
    message: string;
    actionType: string;
  };
}

const initialState: ChatIState = {
  currentConversation: null,
  conversationParticipants: null,
  candidate_offer: [],
  chatConversation: [],
  allConversations: [],
  filteredAllConversations: [],
  customers: [],
  inboxChannel: '',
  chatActiveTab: 0,
  iceServerData: null,
  conversationStatus: '',
  // sdp_offer: null,
  action: {
    status: 'idle',
    error: '',
    message: '',
    actionType: '',
  },
};

export const fetchIceServer = createAsyncThunk(
  '/ecocare.metered.live/api/',
  async (data, thunkAPI) => {
    try {
      const res = await fetch(
        'https://p2p_video.metered.live/api/v1/turn/credentials?apiKey=0b905d84bb32658af5c1f6a23a8278d4293c',
        {
          headers: { 'Cache-Control': 'no-cache' },
        }
      );
      const data = await res.json();
      return { data };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getOnlineUsers = createAsyncThunk('/onlineUsers', async (roomId: string, thunkAPI) => {
  try {
    const response = await signalingClient.get(`/connect/api/room/persons/${roomId}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_SIGNALING_SERVER_API_KEY || '',
      },
    });
    return response;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const deleteSdp = createAsyncThunk(
  '/chat/conversation/delete-sdp',
  async (data: any, thunkAPI) => {
    const { conversationId, message_external_identifier } = data;
    try {
      const response = await client.delete(
        `/chat/conversation/${conversationId}/${message_external_identifier}/delete-sdp`,
        {
          headers: { 'Cache-Control': 'no-cache' },
        }
      );
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getConversations = createAsyncThunk(
  '/chat/conversation',
  async (data: { getFirstConversation: boolean; type: string | number }, thunkAPI) => {
    const route = data.type === '' ? `/chat/conversation` : `/chat/conversation/${data.type}`;
    try {
      const response = await client.get(route, {
        headers: { 'Cache-Control': 'no-cache' },
      });
      return { data: response.data, getFirstConversation: data.getFirstConversation };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getParticipantsByConversation = createAsyncThunk(
  '/chat/participant/conversation',
  async (conversation_id: string, thunkAPI) => {
    try {
      const response = await client.get(`/chat/participant/conversation/${conversation_id}`, {
        headers: { 'Cache-Control': 'no-cache' },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getConversationHistory = createAsyncThunk(
  '/chat/conversation/history',
  async (conversationId: string, thunkAPI) => {
    try {
      const response = await client.get(
        `/chat/conversation/${conversationId}/history/v2?offset=0&limit=100`,
        {
          headers: { 'Cache-Control': 'no-cache' },
        }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const updateChatDoctorName = createAsyncThunk(
  '/chat/person/update',
  async (data: { doctorId: string; personName: string; personLastname: string }, thunkAPI) => {
    try {
      const response = await client.put(`/chat/person/${data.doctorId}`, {
        personName: data.personName,
        personLastname: data.personLastname,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const addChatParticipant = createAsyncThunk(
  '/chat/participant',
  async (data: any, thunkAPI) => {
    try {
      const response = await client.post(`/chat/participant`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getLocationCustomers = createAsyncThunk(
  '/healthpoint/location_customers',
  async (locationId: string, thunkAPI) => {
    try {
      const response = await client.get(`/healthpoint/location_customers/${locationId}`);
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const createCustomerChat = createAsyncThunk(
  '/chat/create-conversation',
  async (data: any, thunkAPI) => {
    try {
      const response = await authorizeClient.post(`/core/chat/room`, data);
      thunkAPI.dispatch(getConversations({ getFirstConversation: true, type: '' }));
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fileAttachment = createAsyncThunk(
  '/chat/fileAttachment',
  async (data: any, thunkAPI) => {
    try {
      const response = await client.post(
        `/chat/message-attachment/upload/${data.conversationId}`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const updateInboxData = (allConversations: any[], msgToUpdate: any) => {
  const res = allConversations;
  const fromIndex = res.map((el: any) => el.conversation_id).indexOf(msgToUpdate.conversation_id);
  const element = res.splice(fromIndex, 1)[0];
  if (element) {
    element.last_payload = msgToUpdate.payload;
    // element.last_payload_created_at = msgToUpdate.created_at;
    res.splice(0, 0, element);
  }

  return res;
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    updateInbox: (state, action) => {
      const updatedData = updateInboxData(state.allConversations, action.payload);
      state.allConversations = updateInboxData(state.allConversations, action.payload);
      state.filteredAllConversations =
        state.inboxChannel !== ''
          ? updatedData.filter((el: any) => el.conversation_type_name === state.inboxChannel)
          : updatedData;
    },
    updateChat: (state, action) => {
      const updatedData = updateInboxData(state.allConversations, action.payload);
      state.chatConversation = [...state.chatConversation, action.payload];
      state.allConversations = updatedData;
      state.filteredAllConversations =
        state.inboxChannel !== ''
          ? updatedData.filter((el: any) => el.conversation_type_name === state.inboxChannel)
          : updatedData;
    },
    updateConversationStatus: (state) => {
      state.currentConversation.conversation_status = 'C';
    },
    updateMessagePayload: (state, action) => {
      const modifiedChatConversations = state.chatConversation.map((obj: any) => {
        if (
          obj.message_external_identifier === action.payload.details.message_external_identifier
        ) {
          return { ...obj, message_offer_payload: null };
        }
        return obj;
      });
      state.chatConversation = modifiedChatConversations;
    },
    updateMessageStatus: (state, action) => {
      const result = state.chatConversation.map((el: any) => {
        if (action.payload.messages_ids.includes(el?.message_external_identifier)) {
          return {
            ...el,
            message_status: [
              ...el.message_status,
              {
                person_id: action.payload.person_id,
                current_status: action.payload.message_status,
              },
            ],
          };
        }
        return { ...el };
      });
      state.chatConversation = result;
    },
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
    },
    setConversationParticipants: (state, action) => {
      state.conversationParticipants = action.payload;
    },
    clearChat: () => {
      return { ...initialState };
    },
    clearAction: (state) => {
      state.action = initialState.action;
    },
    setCandidateOffer: (state, action) => {
      state.candidate_offer = [...state.candidate_offer, action.payload];
      localStorage.setItem('ice_candidate_offer', JSON.stringify(state.candidate_offer));
    },
    filterConversationsByType: (state, action) => {
      const filteredData = action.payload
        ? state.allConversations.filter((el: any) => el.conversation_type_name === action.payload)
        : state.allConversations;
      state.filteredAllConversations = filteredData;
      state.currentConversation = filteredData[0];
    },
    setInboxChannel: (state, action) => {
      state.inboxChannel = action.payload;
    },
    setChatActiveTab: (state, action) => {
      state.chatActiveTab = action.payload;
    },
    // setSDPOffer: (state, action) => {
    //   state.sdp_offer = action.payload;
    // },
  },
  extraReducers(builder) {
    builder.addCase(fetchIceServer.pending, (state, action) => {
      state.action.status = 'loading';
      state.action.actionType = action.type;
    });
    builder.addCase(fetchIceServer.fulfilled, (state, action) => {
      state.iceServerData = action.payload.data;
      state.action.status = 'succeeded';
    });
    builder.addCase(fetchIceServer.rejected, (state, action) => {
      state.action.status = 'failed';
      state.action.error = action.error || 'Something went wrong!';
      state.action.actionType = action.type;
    });
    builder.addCase(getParticipantsByConversation.pending, (state, action) => {
      state.action.status = 'loading';
      state.action.actionType = action.type;
    });
    builder.addCase(getConversations.pending, (state, action) => {
      state.action.status = 'loading';
      state.action.actionType = 'GET_CONVERSATIONS';
    });
    builder.addCase(getConversations.fulfilled, (state, action) => {
      let closedConversations = action.payload.data?.filter(
        (el: any) => el.conversation_status === 'C'
      );
      let otherConversations = action.payload.data?.filter(
        (el: any) => el.conversation_status !== 'C'
      );
      let getFirstConversation = (
        otherConversations: Array<any>,
        closedConversations: Array<any>
      ) => {
        const result = null;
        if (otherConversations.length > 0) return otherConversations[0];
        if (closedConversations.length > 0) return closedConversations[0];
        return result;
      };

      state.allConversations = [...otherConversations, ...closedConversations];
      state.filteredAllConversations = [...otherConversations, ...closedConversations];
      state.currentConversation = action.payload.getFirstConversation
        ? getFirstConversation(otherConversations, closedConversations)
        : state.currentConversation;
      state.action.status = 'succeeded';
      state.action.actionType = 'GET_CONVERSATIONS';
    });
    builder.addCase(getConversations.rejected, (state, action) => {
      state.action.status = 'failed';
      state.action.error = action.error || 'Something went wrong!';
      state.action.actionType = 'GET_CONVERSATIONS';
    });
    builder.addCase(getConversationHistory.pending, (state, action) => {
      state.action.status = 'loading';
      state.action.actionType = 'GET_CONVERSATION_HISTORY';
    });
    builder.addCase(getConversationHistory.fulfilled, (state, action) => {
      state.chatConversation = action.payload || [];
      state.action.status = 'succeeded';
      state.action.actionType = 'GET_CONVERSATION_HISTORY';
    });
    builder.addCase(getConversationHistory.rejected, (state, action) => {
      state.action.status = 'failed';
      state.action.error = action.error || 'Something went wrong!';
      state.action.actionType = 'GET_CONVERSATION_HISTORY';
    });
    builder.addCase(getLocationCustomers.pending, (state) => {
      state.action.status = 'loading';
      state.action.actionType = 'GET_LOCATION_CUSTOMERS';
    });
    builder.addCase(getLocationCustomers.fulfilled, (state, action) => {
      state.action.status = 'succeeded';
      state.customers = action.payload.customers;
      state.action.actionType = 'GET_LOCATION_CUSTOMERS';
    });
    builder.addCase(getLocationCustomers.rejected, (state, action) => {
      state.action.status = 'failed';
      state.action.actionType = 'GET_LOCATION_CUSTOMERS';
      state.action.error = action.error || 'Something went wrong!';
    });
    builder.addCase(createCustomerChat.pending, (state) => {
      state.action.status = 'loading';
      state.action.actionType = 'CREATE_CUSTOMER_CHAT';
    });
    builder.addCase(createCustomerChat.fulfilled, (state) => {
      state.action.status = 'succeeded';
      state.action.actionType = 'CREATE_CUSTOMER_CHAT';
      state.inboxChannel = 'support';
      state.chatActiveTab = 1;
    });
    builder.addCase(createCustomerChat.rejected, (state, action) => {
      state.action.status = 'failed';
      state.action.error = action.error || 'Something went wrong!';
      state.action.actionType = 'CREATE_CUSTOMER_CHAT';
    });
    builder.addCase(fileAttachment.pending, (state) => {
      state.action.status = 'loading';
      state.action.actionType = 'FILE_ATTACHMENT';
    });
    builder.addCase(fileAttachment.fulfilled, (state) => {
      state.action.status = 'succeeded';
      state.action.actionType = 'FILE_ATTACHMENT';
    });
    builder.addCase(fileAttachment.rejected, (state, action) => {
      state.action.status = 'failed';
      state.action.error = action.error || 'Something went wrong!';
      state.action.actionType = 'FILE_ATTACHMENT';
    });
    builder.addCase(getParticipantsByConversation.fulfilled, (state, action) => {
      // state.action.status = 'succeeded';
      state.conversationParticipants = action.payload;
    });
  },
});

export const {
  setConversationParticipants,
  setCurrentConversation,
  updateChat,
  updateConversationStatus,
  clearChat,
  updateMessageStatus,
  updateInbox,
  clearAction,
  setCandidateOffer,
  updateMessagePayload,
  filterConversationsByType,
  setInboxChannel,
  setChatActiveTab,
  // setSDPOffer,
} = chatSlice.actions;

export const selectLocationCustomers = (state: RootState) => state.chat.customers;

export default chatSlice.reducer;
